import { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";
import BtnPrimaryL from "../Buttons/BtnPrimaryL";
import { createCheckoutSession } from "../../api";
import Toast from "../Elements/Toast";
import { annualPriceId, monthlyPriceId } from "../../utils/stripeData";
import PropTypes from "prop-types";

const SubscriptionPlan = ({ hasActiveSubscription }) => {
  const { copy } = useLanguage();
  const [billingPeriod, setBillingPeriod] = useState("yearly"); // "monthly" or "yearly"
  const [toast, setToast] = useState({ message: "", type: "" });

  const plans = {
    monthly: {
      price: "€5.99",
      interval: "month",
      priceId: monthlyPriceId,
      totalYearly: 71.88, // For comparison (5.99 * 12)
    },
    yearly: {
      price: "€59.99",
      interval: "year",
      priceId: annualPriceId,
      totalYearly: 59.99,
      savings: 17, // Percentage saved vs monthly (71.88 - 59.99) / 71.88 * 100
    },
  };

  const currentPlan = plans[billingPeriod];

  const features = [
    copy.pricing.features.unlimited_goals,
    copy.pricing.features.progress_dashboard,
    copy.pricing.features.daily_reminders,
    "Monthly finance tracker",
    copy.pricing.features.vision_board,
    copy.pricing.features.wishlist,
    copy.pricing.features.journaling,
    copy.pricing.features.calendar,
    copy.pricing.features.personalised_focus,
  ];

  const handleSubscribe = async () => {
    try {
      const response = await createCheckoutSession(currentPlan.priceId);
      window.location.href = response.url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setToast({ message: error.message, type: "error" });
    }
  };

  return (
    <div className="px-8 py-10 rounded-lg bg-white card-base-border flex flex-col">
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}

      {/* Title */}
      <div className="text-center pb-8">
        <h2 className="text-2xl sm:text-3xl font-bold">Unlock your full potential</h2>
        <p className="text-sm text-gray-600 mt-1">Start achieving your goals today</p>
      </div>

      {/* Billing Period Toggle */}
      <div className="flex justify-center pb-2">
        <div className="flex flex-row card-base-border rounded-md w-fit">
          <div
            className={`py-2 px-4 cursor-pointer text-sm ${
              billingPeriod === "monthly" ? "bg-accent-light" : "bg-transparent"
            }`}
            onClick={() => setBillingPeriod("monthly")}
          >
            Monthly
          </div>
          <div
            className={`py-2 px-4 cursor-pointer text-sm ${
              billingPeriod === "yearly" ? "bg-accent-light" : "bg-transparent"
            }`}
            onClick={() => setBillingPeriod("yearly")}
          >
            Yearly
          </div>
        </div>
      </div>

      <div className="card-base-border rounded-lg p-6 mb-6 flex flex-col gap-4">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2">
            <p className="text-4xl font-bold">{currentPlan.price}</p>
            <p className="text-gray-600">/{currentPlan.interval}</p>
          </div>
          {billingPeriod === "yearly" && (
            <p className="mt-1 text-sm text-green-600 font-medium">
              2 months free! Billed annually
            </p>
          )}
          {billingPeriod === "monthly" && (
            <p className="mt-1 text-sm text-gray-500">
              Billed monthly
            </p>
          )}
        </div>

        <ul className="space-y-4 md:pr-10 md:pl-10 mb-10">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-2 !mb-2 items-center">
              <FaCheckCircle className="text-primary mr-2" size={15} />
              <p className="md:max-w-70 w-full">{feature}</p>
            </li>
          ))}
        </ul>

        {!hasActiveSubscription && (
          <div className="flex flex-col gap-8 items-center min-h-[60px] justify-center">
            <BtnPrimaryL
              buttonCTA={copy.pricing.subscribe_cta}
              onClickLink={() => handleSubscribe()}
              className=" text-center"
            />
          </div>
        )}
      </div>
    </div>
  );
};

SubscriptionPlan.propTypes = {
  hasActiveSubscription: PropTypes.bool,
};

export default SubscriptionPlan;
